import React, { useState } from 'react';
import { DEFAULT_CITIES, CONFIG } from '../constants';
import { useDebounce } from '../hooks/useDebounce';
import { fetchLocationSuggestions } from '../services/weatherService';

/**
 * Sidebar Component
 * Contains search, popular cities, and recent searches
 */
const Sidebar = ({ 
  isSidebarOpen, 
  onClose, 
  onLocationSelect, 
  lastVisited, 
  onClearHistory,
  onGetLocation 
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const debouncedSearchInput = useDebounce(searchInput, CONFIG.SEARCH_DEBOUNCE_MS);

  // Fetch suggestions when debounced search input changes
  React.useEffect(() => {
    const loadSuggestions = async () => {
      if (debouncedSearchInput.length > CONFIG.MIN_SEARCH_LENGTH) {
        setLoadingSuggestions(true);
        try {
          const results = await fetchLocationSuggestions(debouncedSearchInput);
          setSuggestions(results);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        } finally {
          setLoadingSuggestions(false);
        }
      } else {
        setSuggestions([]);
      }
    };

    loadSuggestions();
  }, [debouncedSearchInput]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      onLocationSelect(searchInput.trim());
      setSearchInput('');
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    onLocationSelect(suggestion);
    setSearchInput('');
    setSuggestions([]);
  };

  const handleCityClick = (cityName) => {
    onLocationSelect(cityName);
  };

  return (
    <div 
      className={`
        fixed md:relative w-full md:w-1/4 h-fit bg-black bg-opacity-30 backdrop-blur-lg
        transform transition-transform duration-300 ease-in-out z-40
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}
    >
      <div className="p-6 overflow-y-auto h-full">
        <div className="p-2 mb-5">
          <h1 className="text-4xl font-bold tracking-tight">GETweath</h1>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <input
              type="text"
              className="w-full p-4 rounded-xl bg-white/10 backdrop-blur-md
                border border-white/20 placeholder-gray-300
                focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Search location..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search location"
            />
            {loadingSuggestions && (
              <div className="absolute right-4 top-4">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              </div>
            )}
          </div>
          
          {/* Suggestions Dropdown */}
          {suggestions.length > 0 && (
            <div className="mt-2 bg-white/10 backdrop-blur-md rounded-lg" role="listbox">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  className="w-full p-3 text-left hover:bg-white/10 transition-colors duration-200"
                  onClick={() => handleSuggestionClick(suggestion)}
                  role="option"
                  aria-selected="false"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </form>

        {/* Geolocation Button */}
        <button
          onClick={onGetLocation}
          className="w-full mb-6 p-3 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 
            transition-colors duration-200 flex items-center justify-center gap-2"
          aria-label="Get current location"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-5 w-5" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
            />
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
            />
          </svg>
          Use Current Location
        </button>

        {/* Popular Cities */}
        <div className="mb-8">
          <h4 className="text-xl font-semibold mb-4">Explore Places</h4>
          <ul className="space-y-3" role="list">
            {DEFAULT_CITIES.map((city) => (
              <li
                key={city.name}
                onClick={() => handleCityClick(city.name)}
                className="p-3 rounded-lg cursor-pointer hover:bg-white hover:bg-opacity-10 
                  transition-colors duration-200 flex items-center space-x-3"
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCityClick(city.name);
                  }
                }}
              >
                <span className="text-2xl" aria-hidden="true">{city.icon}</span>
                <span>{city.name}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Recent Searches */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Recent Searches</h2>
            {lastVisited.length > 0 && (
              <button
                onClick={onClearHistory}
                className="text-sm text-gray-400 hover:text-white transition-colors"
                aria-label="Clear search history"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-2" role="list">
            {lastVisited.length > 0 ? (
              lastVisited.slice(0, 4).map((city) => (
                <button
                  key={city}
                  onClick={() => handleCityClick(city)}
                  className="w-full p-3 rounded-lg hover:bg-white/10
                    transition-colors duration-200 text-left"
                  role="listitem"
                >
                  {city}
                </button>
              ))
            ) : (
              <p className="text-gray-400 p-3">No recent searches</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
